import { useState } from "react";

import { APP_NAME } from "@excalidraw/common";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { TextField } from "@excalidraw/excalidraw/components/TextField";
import { useI18n } from "@excalidraw/excalidraw/i18n";

import { getCollaborationLink } from "../data";
import { resolveRoomCode, RoomCodeServiceError } from "../data/roomCodes";
import "./JoinByCodeDialog.scss";

import type { FormEvent, MouseEvent } from "react";
import type { CollabAPI } from "../collab/Collab";

const ROOM_CODE_LENGTH = 6;
const normalizeCode = (value: string) =>
  value.replace(/[^\d]/g, "").slice(0, ROOM_CODE_LENGTH);

type JoinByCodeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  collabAPI: CollabAPI | null;
};

export const JoinByCodeDialog = ({
  isOpen,
  onClose,
  collabAPI,
}: JoinByCodeDialogProps) => {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) {
    return null;
  }

  const resetStateAndClose = () => {
    setErrorMessage(null);
    setCode("");
    setIsSubmitting(false);
    onClose();
  };

  const handleJoin = async (event?: FormEvent | MouseEvent) => {
    event?.preventDefault();
    if (!collabAPI) {
      setErrorMessage(t("roomDialog.codeJoinErrorGeneric"));
      return;
    }

    if (collabAPI.isCollaborating()) {
      setErrorMessage(t("roomDialog.codeJoinErrorAlreadyCollaborating"));
      return;
    }

    const normalized = normalizeCode(code);

    if (normalized.length !== ROOM_CODE_LENGTH) {
      setErrorMessage(t("roomDialog.codeJoinErrorInvalid"));
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);

      const { roomId, roomKey } = await resolveRoomCode(normalized);
      window.history.pushState(
        {},
        APP_NAME,
        getCollaborationLink({ roomId, roomKey }),
      );
      await collabAPI.startCollaboration(
        { roomId, roomKey },
        { shortCode: normalized },
      );
      resetStateAndClose();
    } catch (error: any) {
      if (error instanceof RoomCodeServiceError) {
        if (error.reason === "CONFIG") {
          setErrorMessage(t("roomDialog.codeJoinErrorGeneric"));
        } else if (error.status === 404) {
          setErrorMessage(t("roomDialog.codeJoinErrorNotFound"));
        } else if (error.status === 400) {
          setErrorMessage(t("roomDialog.codeJoinErrorInvalid"));
        } else {
          setErrorMessage(t("roomDialog.codeJoinErrorGeneric"));
        }
      } else {
        setErrorMessage(t("roomDialog.codeJoinErrorGeneric"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      title={false}
      size="small"
      onCloseRequest={resetStateAndClose}
      className="JoinByCodeDialog"
    >
      <form className="JoinByCodeDialog__form" onSubmit={handleJoin}>
        <div className="JoinByCodeDialog__header">
          <h3>{t("roomDialog.codeJoinTitle")}</h3>
          <p>{t("roomDialog.codeJoinDescription")}</p>
        </div>
        <TextField
          fullWidth
          label={t("roomDialog.codeJoinInputLabel")}
          placeholder="123456"
          inputMode="numeric"
          autoFocus
          value={code}
          onChange={(value) => {
            setErrorMessage(null);
            setCode(normalizeCode(value));
          }}
          maxLength={6}
        />
        {errorMessage && (
          <div className="JoinByCodeDialog__error">{errorMessage}</div>
        )}
        <FilledButton
          size="large"
          label={t("roomDialog.codeJoinButton")}
          status={isSubmitting ? "loading" : null}
          onClick={(event) => {
            if (!code || isSubmitting) {
              event.preventDefault();
              return;
            }
            handleJoin(event);
          }}
        />
      </form>
    </Dialog>
  );
};
